import React, { useMemo, useState } from "react";
import { Box, Container, Stack, Typography } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";

import { useDispatch, useSelector } from "react-redux";
import { createSelector, Dispatch } from "@reduxjs/toolkit";
import { Product } from "../../../lib/types/product";
import { serverApi } from "../../../lib/config";
import { retrievePopularBooks } from "./selector";
import { Link } from "react-router-dom";
import { useGlobals } from "../../hooks/useGlobals";
import ProductService from "../../services/ProductService";
import { sweetErrorHandling, sweetTopSmallSuccessAlert } from "../../../lib/sweetAlert";
import { setPopularBooks as setPopularBooksState } from "./slice";

/** REDUX SLICE & SELECTOR */
const popularBooksRetriever = createSelector(
  retrievePopularBooks,
  (popularBooks) => ({ popularBooks })
);

const actionDispatch = (dispatch: Dispatch) => ({
  setPopularBooks: (data: Product[]) => dispatch(setPopularBooksState(data)),
});

export default function PopularBooks() {
  const { authMember } = useGlobals();
  const dispatch = useDispatch();
  const { setPopularBooks } = useMemo(() => actionDispatch(dispatch), [dispatch]);
  const { popularBooks } = useSelector(popularBooksRetriever);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [hoveredLikeId, setHoveredLikeId] = useState<string | null>(null);

  // Source of truth:
  // - if server provides `meLiked`, it decides (like Products page)
  // - otherwise we fall back to our optimistic `likedIds`
  const isLiked = (book: Product) =>
    (book?.meLiked?.some((m) => m.myFavorite) ?? false) || likedIds.has(book._id);

  const handleLike = async (e: React.MouseEvent, book: Product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!authMember) {
      await sweetErrorHandling({ message: "You are not authenticated, please login first" });
      return;
    }

    const wasLiked = isLiked(book);
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (wasLiked) next.delete(book._id);
      else next.add(book._id);
      return next;
    });

    const nextLikeCount = Math.max(0, (book.productLikes ?? 0) + (wasLiked ? -1 : 1));
    setPopularBooks(
      popularBooks.map((item: Product) =>
        item._id === book._id ? { ...item, productLikes: nextLikeCount } : item
      )
    );

    try {
      const productService = new ProductService();
      const updated = await productService.likeProduct(book._id);
      setHoveredLikeId(null);
      setPopularBooks(
        popularBooks.map((item: Product) => (item._id === book._id ? updated : item))
      );
      await sweetTopSmallSuccessAlert("Success", 800);
    } catch (err) {
      setHoveredLikeId(null);
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (wasLiked) next.add(book._id);
        else next.delete(book._id);
        return next;
      });
      setPopularBooks(
        popularBooks.map((item: Product) => (item._id === book._id ? book : item))
      );
      console.log(err);
    }
  };

  return (
    <div className="popular-books-frame">
      <Container>
        <Stack className="popular-section">
          <Box className="home-section-head">
            <Box className="home-section-titleblock">
              <Box className="category-title">Popular Books</Box>
              <Box className="home-section-subtitle">
                Most viewed picks this week — discover what readers love.
              </Box>
            </Box>
            <Link to="/products" className="home-section-link">
              See all
            </Link>
          </Box>

          <div className="cards-frame">
            <div className="products-page">
              <div className="products-browse">
                <div className="products-grid home-landing-grid">
                  {popularBooks.length !== 0 ? (
                    popularBooks.map((book: Product) => {
                      const imagePath = `${serverApi}/${book.productImages?.[0] ?? ""}`;
                      const collectionSlug = (book.productCollection ?? "other")
                        .toLowerCase()
                        .replace("_", "-");

                      return (
                        <Link
                          key={book._id}
                          to={`/products/${book._id}`}
                          className="home-card-link home-product-card-link"
                        >
                          <article
                            className={`product-card product-card-book product-card--${collectionSlug} home-product-card`}
                          >
                            <div className="product-card-image-wrap">
                              <div
                                className="product-card-image"
                                style={{ backgroundImage: `url(${imagePath})` }}
                              />
                            </div>

                            <Typography variant="subtitle1" className="product-card-title">
                              {book.productName}
                            </Typography>
                            {book.productAuthor && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                className="product-card-author"
                              >
                                {book.productAuthor}
                              </Typography>
                            )}
                            <div className="product-card-divider" />
                            <div className="product-card-footer">
                              <div className="product-card-footer-left">
                                <Typography variant="body1" className="product-card-price">
                                  ${Number(book.productPrice ?? 0).toFixed(2)}
                                </Typography>
                              </div>
                              <div className="product-card-footer-divider" />
                              <div className="product-card-footer-stats">
                                <span className="product-card-views">
                                  <RemoveRedEyeIcon sx={{ fontSize: 22 }} />
                                  <span className="product-card-stat-num">{book.productViews ?? 0}</span>
                                </span>

                                <button
                                  type="button"
                                  className={`product-card-likes ${isLiked(book) ? "product-card-liked" : ""}`}
                                  onClick={(e) => handleLike(e, book)}
                                  onMouseEnter={() => setHoveredLikeId(book._id)}
                                  onMouseLeave={() => setHoveredLikeId(null)}
                                  onTouchEnd={() => setHoveredLikeId(null)}
                                  aria-label={isLiked(book) ? "Unlike" : "Like"}
                                >
                                  {isLiked(book) ||
                                  (hoveredLikeId === book._id &&
                                    typeof window !== "undefined" &&
                                    window.matchMedia("(hover: hover) and (pointer: fine)").matches) ? (
                                    <FavoriteIcon sx={{ fontSize: 22 }} />
                                  ) : (
                                    <FavoriteBorderIcon sx={{ fontSize: 22 }} />
                                  )}
                                  <span className="product-card-stat-num">{book.productLikes ?? 0}</span>
                                </button>
                              </div>
                            </div>
                          </article>
                        </Link>
                      );
                    })
                  ) : (
                    <Box className="no-data"> Popular Books Are Not Available!</Box>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Stack>
      </Container>
    </div>
  );
}
